<template>
  <div class="payment-methods-config">
    <!-- Header with Add button -->
    <div class="section-header">
      <h2 class="section-title">Payment Methods</h2>
      <button class="btn btn-primary" @click="openAddModal">+ Add Method</button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <span class="spinner"></span>
      Loading payment methods...
    </div>

    <!-- Methods List -->
    <div v-else-if="methods.length > 0" class="methods-list">
      <div
        v-for="method in methods"
        :key="method.id"
        class="method-card"
        :class="{ trashed: method.trashed }"
      >
        <div class="method-main">
          <div class="method-info">
            <div class="method-name">{{ method.display_name }}</div>
            <div class="method-meta">
              <span class="method-provider">{{ method.provider }}</span>
              <span class="type-badge" :class="'type-' + method.type">{{ method.type }}</span>
            </div>
          </div>
          <div class="method-fees">
            <span v-if="hasFees(method)" class="fee-label">
              Deposit: {{ formatFee(method.deposit_fee_flat, method.deposit_fee_percent) }}
              &middot;
              Withdrawal: {{ formatFee(method.withdrawal_fee_flat, method.withdrawal_fee_percent) }}
            </span>
            <span v-else class="fee-label fee-none">No fees</span>
          </div>
          <div class="method-limits">
            <span v-if="hasLimits(method)" class="limit-label">
              Deposit: {{ formatRange(method.min_deposit, method.max_deposit) }}
              &middot;
              Withdrawal: {{ formatRange(method.min_withdrawal, method.max_withdrawal) }}
            </span>
          </div>
          <div class="method-actions">
            <label v-if="!method.trashed" class="toggle" @click.stop>
              <input
                type="checkbox"
                :checked="method.enabled"
                :disabled="togglingId === method.id"
                @change="handleToggle(method)"
              >
              <span class="toggle-slider"></span>
            </label>
            <span v-else class="trashed-label">Deleted</span>

            <button
              v-if="!method.trashed"
              class="btn-icon"
              title="Edit"
              @click="openEditModal(method)"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button
              v-if="!method.trashed"
              class="btn-icon btn-icon-danger"
              title="Delete"
              @click="confirmDelete(method)"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="empty-state">
      <p>No payment methods configured yet.</p>
      <button class="btn btn-primary" @click="openAddModal">+ Add Payment Method</button>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="modal.visible" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content modal-lg">
        <h3 class="modal-title">{{ modal.editing ? 'Edit Payment Method' : 'Add Payment Method' }}</h3>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Provider Slug</label>
            <input
              v-model="modal.form.provider"
              type="text"
              class="form-input"
              placeholder="e.g. manual, crypto, stripe"
              :disabled="modal.editing"
            >
          </div>
          <div class="form-group">
            <label class="form-label">Display Name</label>
            <input
              v-model="modal.form.display_name"
              type="text"
              class="form-input"
              placeholder="e.g. Bank Transfer"
            >
          </div>
        </div>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Type</label>
            <select v-model="modal.form.type" class="form-input">
              <option value="deposit">Deposit Only</option>
              <option value="withdrawal">Withdrawal Only</option>
              <option value="both">Both</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Sort Order</label>
            <input
              v-model.number="modal.form.sort_order"
              type="number"
              class="form-input"
              min="0"
              placeholder="0"
            >
          </div>
        </div>

        <h4 class="modal-section-title">Deposit Limits &amp; Fees</h4>
        <div class="form-grid form-grid-4">
          <div class="form-group">
            <label class="form-label">Min Deposit</label>
            <input
              v-model="modal.form.min_deposit"
              type="text"
              class="form-input"
              placeholder="0.00"
            >
          </div>
          <div class="form-group">
            <label class="form-label">Max Deposit</label>
            <input
              v-model="modal.form.max_deposit"
              type="text"
              class="form-input"
              placeholder="0.00"
            >
          </div>
          <div class="form-group">
            <label class="form-label">Fee (flat)</label>
            <input
              v-model="modal.form.deposit_fee_flat"
              type="text"
              class="form-input"
              placeholder="0.00"
            >
          </div>
          <div class="form-group">
            <label class="form-label">Fee (%)</label>
            <input
              v-model="modal.form.deposit_fee_percent"
              type="text"
              class="form-input"
              placeholder="0.00"
            >
          </div>
        </div>

        <h4 class="modal-section-title">Withdrawal Limits &amp; Fees</h4>
        <div class="form-grid form-grid-4">
          <div class="form-group">
            <label class="form-label">Min Withdrawal</label>
            <input
              v-model="modal.form.min_withdrawal"
              type="text"
              class="form-input"
              placeholder="0.00"
            >
          </div>
          <div class="form-group">
            <label class="form-label">Max Withdrawal</label>
            <input
              v-model="modal.form.max_withdrawal"
              type="text"
              class="form-input"
              placeholder="0.00"
            >
          </div>
          <div class="form-group">
            <label class="form-label">Fee (flat)</label>
            <input
              v-model="modal.form.withdrawal_fee_flat"
              type="text"
              class="form-input"
              placeholder="0.00"
            >
          </div>
          <div class="form-group">
            <label class="form-label">Fee (%)</label>
            <input
              v-model="modal.form.withdrawal_fee_percent"
              type="text"
              class="form-input"
              placeholder="0.00"
            >
          </div>
        </div>

        <h4 class="modal-section-title">Configuration (JSON)</h4>
        <div class="config-section">
          <div
            v-for="(entry, index) in modal.configEntries"
            :key="index"
            class="config-row"
          >
            <input
              v-model="entry.key"
              type="text"
              class="form-input config-key"
              placeholder="Key"
            >
            <div class="config-value-wrap">
              <input
                v-if="entry.masked"
                type="password"
                :value="entry.value"
                class="form-input"
                placeholder="Enter new value to update"
                @input="onConfigValueInput(index, ($event.target as HTMLInputElement).value)"
                @focus="entry.focused = true"
                @blur="entry.focused = false"
              >
              <input
                v-else
                v-model="entry.value"
                type="text"
                class="form-input"
                placeholder="Value"
              >
              <button
                v-if="isSensitiveKey(entry.key)"
                class="btn-icon-sm"
                :title="entry.masked ? 'Show' : 'Hide'"
                @click="entry.masked = !entry.masked"
              >
                {{ entry.masked ? '&#128065;' : '&#128064;' }}
              </button>
            </div>
            <button class="btn-icon-sm btn-icon-danger" title="Remove" @click="removeConfigEntry(index)">
              &times;
            </button>
          </div>
          <button class="btn-link" @click="addConfigEntry">+ Add config field</button>
        </div>

        <div v-if="modal.error" class="modal-error">{{ modal.error }}</div>

        <div class="modal-actions">
          <button class="btn btn-secondary" @click="closeModal">Cancel</button>
          <button
            class="btn btn-primary"
            :disabled="modal.saving"
            @click="saveMethod"
          >
            <span v-if="modal.saving" class="spinner spinner-sm"></span>
            {{ modal.editing ? 'Save Changes' : 'Create Method' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deleteModal.visible" class="modal-overlay" @click.self="closeDeleteModal">
      <div class="modal-content">
        <h3 class="modal-title">Delete Payment Method</h3>
        <p class="modal-subtitle">
          Are you sure you want to delete <strong>{{ deleteModal.name }}</strong>?
          This will soft-delete the method and disable it for all users.
        </p>
        <div v-if="deleteModal.error" class="modal-error">{{ deleteModal.error }}</div>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="closeDeleteModal">Cancel</button>
          <button
            class="btn btn-danger"
            :disabled="deleteModal.loading"
            @click="handleDelete"
          >
            <span v-if="deleteModal.loading" class="spinner spinner-sm"></span>
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

interface PaymentMethodData {
  id: number
  provider: string
  display_name: string
  type: string
  enabled: boolean
  min_deposit: string | null
  max_deposit: string | null
  min_withdrawal: string | null
  max_withdrawal: string | null
  deposit_fee_flat: string | null
  deposit_fee_percent: string | null
  withdrawal_fee_flat: string | null
  withdrawal_fee_percent: string | null
  config: Record<string, string> | null
  sort_order: number
  trashed: boolean
}

interface ConfigEntry {
  key: string
  value: string
  masked: boolean
  focused: boolean
}

const toast = useToastStore()

const methods = ref<PaymentMethodData[]>([])
const loading = ref(false)
const togglingId = ref<number | null>(null)

const modal = ref({
  visible: false,
  editing: false,
  editId: 0,
  saving: false,
  error: '',
  form: {
    provider: '',
    display_name: '',
    type: 'both' as string,
    sort_order: 0,
    min_deposit: '',
    max_deposit: '',
    min_withdrawal: '',
    max_withdrawal: '',
    deposit_fee_flat: '',
    deposit_fee_percent: '',
    withdrawal_fee_flat: '',
    withdrawal_fee_percent: '',
  },
  configEntries: [] as ConfigEntry[],
})

const deleteModal = ref({
  visible: false,
  id: 0,
  name: '',
  loading: false,
  error: '',
})

function isSensitiveKey(key: string): boolean {
  return /key|secret|token/i.test(key)
}

function hasFees(m: PaymentMethodData): boolean {
  return (
    parseFloat(m.deposit_fee_flat || '0') > 0 ||
    parseFloat(m.deposit_fee_percent || '0') > 0 ||
    parseFloat(m.withdrawal_fee_flat || '0') > 0 ||
    parseFloat(m.withdrawal_fee_percent || '0') > 0
  )
}

function hasLimits(m: PaymentMethodData): boolean {
  return (
    parseFloat(m.min_deposit || '0') > 0 ||
    parseFloat(m.max_deposit || '0') > 0 ||
    parseFloat(m.min_withdrawal || '0') > 0 ||
    parseFloat(m.max_withdrawal || '0') > 0
  )
}

function formatFee(flat: string | null, percent: string | null): string {
  const parts: string[] = []
  const f = parseFloat(flat || '0')
  const p = parseFloat(percent || '0')
  if (f > 0) parts.push('$' + f.toFixed(2))
  if (p > 0) parts.push(p + '%')
  return parts.length > 0 ? parts.join(' + ') : 'Free'
}

function formatRange(min: string | null, max: string | null): string {
  const lo = parseFloat(min || '0')
  const hi = parseFloat(max || '0')
  if (lo > 0 && hi > 0) return '$' + lo.toFixed(2) + ' - $' + hi.toFixed(2)
  if (lo > 0) return 'Min $' + lo.toFixed(2)
  if (hi > 0) return 'Max $' + hi.toFixed(2)
  return 'No limits'
}

async function fetchMethods() {
  loading.value = true
  try {
    const response = await apiClient.get('/admin/api/payment-methods')
    methods.value = response.data.data
  } catch {
    toast.error('Failed to load payment methods')
  } finally {
    loading.value = false
  }
}

async function handleToggle(method: PaymentMethodData) {
  togglingId.value = method.id
  try {
    await apiClient.put(`/admin/api/payment-methods/${method.id}`, {
      provider: method.provider,
      display_name: method.display_name,
      type: method.type,
      enabled: !method.enabled,
    })
    method.enabled = !method.enabled
    toast.success(`${method.display_name} ${method.enabled ? 'enabled' : 'disabled'}`)
  } catch {
    toast.error('Failed to update payment method')
  } finally {
    togglingId.value = null
  }
}

function openAddModal() {
  modal.value = {
    visible: true,
    editing: false,
    editId: 0,
    saving: false,
    error: '',
    form: {
      provider: '',
      display_name: '',
      type: 'both',
      sort_order: 0,
      min_deposit: '',
      max_deposit: '',
      min_withdrawal: '',
      max_withdrawal: '',
      deposit_fee_flat: '',
      deposit_fee_percent: '',
      withdrawal_fee_flat: '',
      withdrawal_fee_percent: '',
    },
    configEntries: [],
  }
}

function openEditModal(method: PaymentMethodData) {
  const entries: ConfigEntry[] = []
  if (method.config) {
    for (const [key, value] of Object.entries(method.config)) {
      const sensitive = isSensitiveKey(key)
      entries.push({
        key,
        value: sensitive ? '' : String(value),
        masked: sensitive,
        focused: false,
      })
    }
  }

  modal.value = {
    visible: true,
    editing: true,
    editId: method.id,
    saving: false,
    error: '',
    form: {
      provider: method.provider,
      display_name: method.display_name,
      type: method.type,
      sort_order: method.sort_order,
      min_deposit: method.min_deposit || '',
      max_deposit: method.max_deposit || '',
      min_withdrawal: method.min_withdrawal || '',
      max_withdrawal: method.max_withdrawal || '',
      deposit_fee_flat: method.deposit_fee_flat || '',
      deposit_fee_percent: method.deposit_fee_percent || '',
      withdrawal_fee_flat: method.withdrawal_fee_flat || '',
      withdrawal_fee_percent: method.withdrawal_fee_percent || '',
    },
    configEntries: entries,
  }
}

function closeModal() {
  modal.value.visible = false
}

function addConfigEntry() {
  modal.value.configEntries.push({ key: '', value: '', masked: false, focused: false })
}

function removeConfigEntry(index: number) {
  modal.value.configEntries.splice(index, 1)
}

function onConfigValueInput(index: number, value: string) {
  modal.value.configEntries[index].value = value
}

function buildConfigPayload(): Record<string, string> | null {
  const entries = modal.value.configEntries.filter((e) => e.key.trim())
  if (entries.length === 0) return null

  const config: Record<string, string> = {}
  for (const entry of entries) {
    // Skip masked entries with empty value (unchanged sensitive fields)
    if (entry.masked && !entry.value) continue
    config[entry.key.trim()] = entry.value
  }
  return Object.keys(config).length > 0 ? config : null
}

async function saveMethod() {
  modal.value.saving = true
  modal.value.error = ''

  const payload: Record<string, unknown> = {
    provider: modal.value.form.provider,
    display_name: modal.value.form.display_name,
    type: modal.value.form.type,
    sort_order: modal.value.form.sort_order,
  }

  // Only include numeric fields if they have a value
  const numericFields = [
    'min_deposit',
    'max_deposit',
    'min_withdrawal',
    'max_withdrawal',
    'deposit_fee_flat',
    'deposit_fee_percent',
    'withdrawal_fee_flat',
    'withdrawal_fee_percent',
  ] as const

  for (const field of numericFields) {
    const val = modal.value.form[field]
    if (val !== '' && val !== null) {
      payload[field] = parseFloat(val)
    } else {
      payload[field] = null
    }
  }

  const config = buildConfigPayload()
  if (config) {
    payload.config = config
  }

  try {
    if (modal.value.editing) {
      await apiClient.put(`/admin/api/payment-methods/${modal.value.editId}`, payload)
      toast.success('Payment method updated')
    } else {
      await apiClient.post('/admin/api/payment-methods', payload)
      toast.success('Payment method created')
    }
    closeModal()
    await fetchMethods()
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
    if (err.response?.data?.errors) {
      const messages = Object.values(err.response.data.errors).flat()
      modal.value.error = messages.join('. ')
    } else {
      modal.value.error = err.response?.data?.message || 'Failed to save payment method'
    }
  } finally {
    modal.value.saving = false
  }
}

function confirmDelete(method: PaymentMethodData) {
  deleteModal.value = {
    visible: true,
    id: method.id,
    name: method.display_name,
    loading: false,
    error: '',
  }
}

function closeDeleteModal() {
  deleteModal.value.visible = false
}

async function handleDelete() {
  deleteModal.value.loading = true
  deleteModal.value.error = ''

  try {
    await apiClient.delete(`/admin/api/payment-methods/${deleteModal.value.id}`)
    toast.success('Payment method deleted')
    closeDeleteModal()
    await fetchMethods()
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } } }
    deleteModal.value.error = err.response?.data?.message || 'Failed to delete payment method'
  } finally {
    deleteModal.value.loading = false
  }
}

onMounted(() => {
  fetchMethods()
})
</script>

<style scoped>
/* Section Header */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn-primary {
  background: var(--accent-primary, #00e701);
  color: #000;
}

.btn-primary:hover:not(:disabled) {
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover {
  border-color: rgba(255, 255, 255, 0.2);
  color: var(--text-primary, #fff);
}

.btn-danger {
  background: var(--color-danger, #ed4245);
  color: #fff;
}

.btn-danger:hover:not(:disabled) {
  box-shadow: 0 0 24px rgba(237, 66, 69, 0.35);
  transform: translateY(-1px);
}

.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-link {
  background: none;
  border: none;
  color: var(--accent-secondary, #1475e1);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 0;
  font-family: inherit;
}

.btn-link:hover {
  text-decoration: underline;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-tertiary, #7f8fa4);
}

.btn-icon:hover {
  color: var(--text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.2);
}

.btn-icon-danger:hover {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
  border-color: rgba(237, 66, 69, 0.3);
}

.btn-icon-sm {
  width: 28px;
  height: 28px;
  border-radius: var(--radius-sm, 4px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
  flex-shrink: 0;
}

.btn-icon-sm:hover {
  color: var(--text-primary, #fff);
}

.btn-icon-sm.btn-icon-danger:hover {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

/* Loading / Empty */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
  background: var(--bg-secondary, #1a2c38);
  border-radius: var(--radius-lg, 12px);
}

.empty-state {
  text-align: center;
  padding: 48px 20px;
  background: var(--bg-secondary, #1a2c38);
  border-radius: var(--radius-lg, 12px);
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

.empty-state p {
  margin: 0 0 16px;
}

/* Method Cards */
.methods-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.method-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-lg, 12px);
  padding: 16px 20px;
  transition: border-color 150ms ease;
}

.method-card:hover {
  border-color: rgba(255, 255, 255, 0.12);
}

.method-card.trashed {
  opacity: 0.5;
}

.method-main {
  display: flex;
  align-items: center;
  gap: 20px;
}

.method-info {
  flex: 1;
  min-width: 0;
}

.method-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin-bottom: 4px;
}

.method-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.method-provider {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
}

.type-badge {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: var(--radius-full, 9999px);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.type-deposit {
  background: rgba(0, 231, 1, 0.12);
  color: var(--color-success, #00e701);
}

.type-withdrawal {
  background: rgba(237, 66, 69, 0.12);
  color: var(--color-danger, #ed4245);
}

.type-both {
  background: rgba(20, 117, 225, 0.12);
  color: var(--accent-secondary, #1475e1);
}

.method-fees,
.method-limits {
  flex-shrink: 0;
  text-align: right;
}

.fee-label,
.limit-label {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
}

.fee-none {
  color: var(--text-tertiary, #7f8fa4);
}

.method-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.trashed-label {
  font-size: 12px;
  color: var(--color-danger, #ed4245);
  font-weight: 600;
}

/* Toggle */
.toggle {
  position: relative;
  display: inline-flex;
  width: 44px;
  height: 24px;
  background: var(--bg-tertiary, #213743);
  border-radius: 9999px;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle input {
  display: none;
}

.toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: var(--text-tertiary, #7f8fa4);
  border-radius: 50%;
  transition: all 150ms ease;
}

.toggle input:checked + .toggle-slider {
  background: var(--accent-primary, #00e701);
  left: 22px;
}

/* Forms */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-grid-4 {
  grid-template-columns: 1fr 1fr 1fr 1fr;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-secondary, #b1bad3);
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 9px 12px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-sm, 4px);
  color: var(--text-primary, #fff);
  font-size: 13px;
  font-family: inherit;
  transition: border-color 150ms ease;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.form-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-input option {
  background: var(--bg-secondary, #1a2c38);
}

/* Config section */
.config-section {
  margin-bottom: 16px;
}

.config-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.config-key {
  flex: 0 0 140px;
}

.config-value-wrap {
  flex: 1;
  display: flex;
  gap: 4px;
  align-items: center;
}

.config-value-wrap .form-input {
  flex: 1;
}

/* Modal section titles */
.modal-section-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-secondary, #b1bad3);
  margin: 8px 0 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-lg, 12px);
  padding: 24px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  max-height: 90vh;
  overflow-y: auto;
}

.modal-lg {
  max-width: 640px;
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 16px;
}

.modal-subtitle {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  margin: 0 0 16px;
  line-height: 1.5;
}

.modal-error {
  padding: 8px 12px;
  background: rgba(237, 66, 69, 0.08);
  border: 1px solid rgba(237, 66, 69, 0.2);
  border-radius: var(--radius-md, 8px);
  color: var(--color-danger, #ed4245);
  font-size: 13px;
  margin-bottom: 16px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner-sm {
  width: 14px;
  height: 14px;
  border-width: 1.5px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 1024px) {
  .method-main {
    flex-wrap: wrap;
  }

  .method-fees,
  .method-limits {
    text-align: left;
  }

  .form-grid-4 {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-grid-4 {
    grid-template-columns: 1fr;
  }

  .method-main {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
